import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { CheckCircle, ShieldCheck, WarningCircle, Spinner } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

/**
 * /verify-email?token=…
 * Stateless: anyone with the link can hit this page on any device. We POST
 * the token to /api/auth/verify-email; success flips the user's
 * `email_verified` flag in the DB, which the original device picks up via the
 * /pending-verification polling loop.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "verifying" : "missing");
  const [error, setError] = useState("");
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("success");
        // If the user happens to be logged in on this device, refresh.
        try { await refresh(); } catch { /* ignore */ }
      } catch (e) {
        setStatus("error");
        setError(formatApiError(e.response?.data?.detail) || "Verification failed.");
      }
    })();
  }, [token, refresh]);

  const dashboardHref = user
    ? (user.role === "seller" ? "/dashboard/seller"
      : user.role === "admin" ? "/dashboard/admin"
      : "/dashboard/buyer")
    : "/login";

  return (
    <div className="min-h-screen bg-mv-secondary text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-block mb-10" aria-label="MarketVyne home">
          <Logo variant="full" height={36} />
        </Link>
        <div className="bg-white text-mv-secondary rounded-3xl p-8 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.45)]"
             data-testid="verify-email-card">
          {status === "verifying" && (
            <div className="space-y-3 text-center" data-testid="verify-state-verifying">
              <Spinner size={36} weight="duotone" className="mx-auto text-mv-primary animate-spin" />
              <div className="font-display font-black text-2xl">Verifying your email…</div>
              <p className="text-sm text-gray-500">This usually takes a second.</p>
            </div>
          )}
          {status === "missing" && (
            <div className="space-y-3 text-center" data-testid="verify-state-missing">
              <WarningCircle size={36} weight="duotone" className="mx-auto text-amber-500" />
              <div className="font-display font-black text-2xl">No verification link</div>
              <p className="text-sm text-gray-500">
                Open the verification email and tap the button there. Check your spam folder if you
                can&apos;t find it.
              </p>
              <Link to="/login" className="inline-block mt-3 text-mv-primary font-bold text-sm">
                Back to log in
              </Link>
            </div>
          )}
          {status === "success" && (
            <div className="space-y-4 text-center" data-testid="verify-state-success">
              <CheckCircle size={44} weight="duotone" className="mx-auto text-mv-primary" />
              <div className="font-display font-black text-2xl">Email verified</div>
              <p className="text-sm text-gray-500">
                You&apos;re all set. You can close this tab — if you started on another device, it will
                continue automatically.
              </p>
              <Button
                data-testid="verify-continue-btn"
                onClick={() => nav(dashboardHref)}
                className="w-full bg-mv-primary hover:bg-mv-primary-light text-white font-bold h-12 rounded-xl"
              >
                Continue
              </Button>
              <div className="text-xs text-gray-400 inline-flex items-center gap-1 justify-center">
                <ShieldCheck size={14} weight="duotone" className="text-mv-primary" />
                Account protected
              </div>
            </div>
          )}
          {status === "error" && (
            <div className="space-y-3 text-center" data-testid="verify-state-error">
              <WarningCircle size={40} weight="duotone" className="mx-auto text-red-500" />
              <div className="font-display font-black text-2xl">Verification failed</div>
              <p className="text-sm text-gray-500">{error}</p>
              <p className="text-xs text-gray-500">
                The link may have expired or already been used. Log in to request a fresh one.
              </p>
              <Link to="/login" className="inline-block mt-3 text-mv-primary font-bold text-sm">
                Log in to resend
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
